import type { FlagProps } from "@wayflyer/flyui";
import { Callout, DropdownField, Flag, Flex, Link, Modal, Text } from "@wayflyer/flyui";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { useIntl } from "react-intl";
import type { BankConnectCountryConfig } from "../connect-bank-button";
import styles from "./country-selection-modal.module.css";

export interface CountrySelectionModalProps {
  titleText: string;
  selectLabel: string;
  options: BankConnectCountryConfig[];
  placeholder?: string;
  initSelectedItem?: BankConnectCountryConfig;
  confirmLabel?: string;
  isOpen?: boolean;
  isLoading?: boolean;
  closeOnConfirm?: boolean;
  showOpenBankingNotice?: boolean;
  onConfirm: ((data: any) => Promise<void>) | ((data: any) => void);
  onSelect?: ((data: any) => Promise<void>) | ((data: any) => void);
  close: () => void;
  /** Called when the modal is dismissed without confirming (Cancel / X / Escape / backdrop). */
  onCancel?: () => void;
}

type TopTextProps = {
  providerName?: string;
  requiresBankStatements?: boolean;
  isDefault?: boolean;
  showOpenBankingNotice?: boolean;
};

const BottomText = ({ requiresBankStatements, isDefault, providerName, showOpenBankingNotice }: TopTextProps) => {
  const intl = useIntl();
  return (
    <>
      {!requiresBankStatements && !isDefault && (
        <>
          {showOpenBankingNotice ? (
            <Callout
              variant="info"
              title="We use Basiq to connect with Open Banking"
              description={
                <Flex direction="column" gap="0">
                  <Text>
                    {intl.formatMessage(
                      {
                        id: "country selection modal: opt in to connect open banking",
                        defaultMessage:
                          "You may need to <b>opt-in to data sharing</b> with your bank before connecting.",
                        description:
                          "Pre-connection message for Australian Open Banking, informing of the requirement to enable data sharing on the bank side",
                      },
                      {
                        b: (chunks: ReactNode[]) => (
                          <Text element="span" fontWeight="semiBold">
                            {chunks}
                          </Text>
                        ),
                      }
                    )}
                  </Text>
                  <Text>
                    {intl.formatMessage(
                      {
                        id: "country selection modal: data sharing help link",
                        defaultMessage: "See our <a>Data Sharing Help</a> page for details.",
                        description: "Link to our Data Sharing Help page",
                      },
                      {
                        a: (chunks: ReactNode[]) => (
                          <Link
                            url="https://help-center.wayflyer.com/en/articles/361036-how-do-i-opt-in-to-data-sharing-in-australia"
                            external
                          >
                            {chunks}
                          </Link>
                        ),
                      }
                    )}
                  </Text>
                </Flex>
              }
            />
          ) : (
            <div className={styles.notice}>
              <Text>
                {intl.formatMessage({ id: "country selection modal: We use ", defaultMessage: "We use " })}{" "}
                <b>{providerName}</b>{" "}
                {intl.formatMessage({
                  id: "country selection modal:  to securely connect to your bank.",
                  defaultMessage: " to securely connect to your bank.",
                })}
              </Text>
            </div>
          )}
          <div className={styles.notice}>
            <Text>
              {intl.formatMessage({
                id: "country selection modal: By proceeding, you consent to Wayflyer processing your bank data in line with our",
                defaultMessage: "By proceeding, you consent to Wayflyer processing your bank data in line with our",
              })}
              &nbsp;
              <Link url="https://www.wayflyer.com/privacy-notice#terms-of-business" external>
                {intl.formatMessage({
                  id: "country selection modal: Privacy Notice",
                  defaultMessage: "Privacy Notice",
                })}
              </Link>
              .
            </Text>
          </div>
        </>
      )}
      {requiresBankStatements && (
        <div className={styles.notice}>
          <Text>
            {intl.formatMessage({
              id: "country selection modal: upload last 6 months of statement",
              defaultMessage:
                "Based on your selected country, we can't connect directly to your bank. You will need to upload your last 6 months of statements instead.",
            })}
          </Text>
        </div>
      )}
    </>
  );
};

/**
 * CountrySelectionModal component
 */
export const CountrySelectionModal = ({
  titleText,
  selectLabel,
  options,
  placeholder,
  initSelectedItem,
  confirmLabel,
  isOpen = false,
  isLoading = false,
  closeOnConfirm = true,
  showOpenBankingNotice = false,
  onConfirm,
  onSelect,
  close,
  onCancel,
}: CountrySelectionModalProps) => {
  const [selectedItem, setSelectedItem] = useState(initSelectedItem);
  const intl = useIntl();

  const countryOptions = useMemo(
    () =>
      options?.map((item) => ({
        ...item,
        text: item.name,
        id: item.code,
        icon: <Flag code={item.code as FlagProps["code"]} />,
      })),
    [options]
  );

  const handleChange = (id) => {
    const item = countryOptions.find((item) => item.id === id);
    setSelectedItem(item);
    onSelect && onSelect(item?.code ?? item);
  };

  const handleConfirm = useCallback(() => {
    if (selectedItem && !isLoading) {
      if (!closeOnConfirm) {
        onConfirm(selectedItem);
        return;
      }
      close();
      setSelectedItem(undefined);
      setTimeout(() => {
        onConfirm(selectedItem);
      }, 300);
    }
  }, [onConfirm, selectedItem, close, closeOnConfirm, isLoading]);

  const handleCancel = useCallback(() => {
    setSelectedItem(undefined);
    close();
    onCancel?.();
  }, [close, onCancel]);

  // TODO handle this better with confirmLabel
  const selectedConfirmLabel = selectedItem?.confirmEmail
    ? intl.formatMessage({ id: "country selection modal: Upload statements", defaultMessage: "Upload statements" })
    : (confirmLabel ?? intl.formatMessage({ id: "country selection modal: Continue", defaultMessage: "Continue" }));
  return (
    <Modal
      onClose={handleCancel}
      title={titleText}
      primaryAction={{
        content: selectedConfirmLabel,
        loading: isLoading,
        onAction: handleConfirm,
        disabled: !selectedItem,
      }}
      isOpen={isOpen}
    >
      <Flex direction="column" gap="4">
        <DropdownField
          label={intl.formatMessage({ id: "country selection modal: Bank location", defaultMessage: "Bank location" })}
          name={selectLabel}
          placeholder={placeholder}
          initialValue={initSelectedItem?.code}
          options={countryOptions}
          onChange={handleChange}
        />
        <BottomText
          requiresBankStatements={selectedItem?.confirmEmail}
          isDefault={!selectedItem}
          providerName={selectedItem?.providerName}
          showOpenBankingNotice={selectedItem?.code === "AU" && showOpenBankingNotice}
        />
      </Flex>
    </Modal>
  );
};
